import React, { useState, useEffect } from 'react';
import {
  subscribeToNotifications,
  unsubscribeFromNotifications,
  isUserSubscribed
} from '../utils/notifications';
import { isSupported } from 'firebase/messaging';
import './AccessibilityBar.css';

const AccessibilityBar = () => {
  // -------------------- Theme and Font Size Initialization --------------------

  // Get initial theme preference from localStorage or system setting
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) return savedTheme === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  // Get initial font size from localStorage or default to 16px
  const getInitialFontSize = () => {
    const savedFont = localStorage.getItem('fontSize');
    return savedFont ? parseInt(savedFont) : 16;
  };

  // -------------------- State Variables --------------------

  const [darkMode, setDarkMode] = useState(getInitialTheme); // Theme toggle
  const [fontSize, setFontSize] = useState(getInitialFontSize); // Font size control
  const [isSubscribed, setIsSubscribed] = useState(null); // Notification subscription status
  const [isProcessing, setIsProcessing] = useState(false); // Spinner for subscribe/unsubscribe
  const [isMessagingSupported, setIsMessagingSupported] = useState(true); // Hide notification if unsupported
  const DEFAULT_FONT_SIZE = 16;

  // -------------------- Apply Theme and Font Size --------------------

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}px`;
    document.body.classList.toggle('dark-theme', darkMode);
    localStorage.setItem('darkMode', darkMode);
    localStorage.setItem('fontSize', fontSize);
  }, [darkMode, fontSize]);

  // -------------------- Check Notification Support and Subscription --------------------

  useEffect(() => {
    async function checkSubscription() {
      const supported = await isSupported();
      setIsMessagingSupported(supported);

      if (!supported) {
        console.warn('Browser does not support Firebase Messaging.');
        return; // Skip subscription check
      }

      try {
        const subscribed = await isUserSubscribed();
        setIsSubscribed(subscribed);
      } catch (err) {
        console.error('Error checking subscription:', err);
        setIsSubscribed(false);
      }
    }

    checkSubscription();
  }, []);

  // -------------------- Subscribe to Notifications --------------------

  const handleSubscribe = async () => {
    const supported = await isSupported();
    if (!supported) {
      alert('Your browser does not support job notifications.');
      return;
    }

    try {
      setIsProcessing(true);
      await subscribeToNotifications();
      const confirmed = await isUserSubscribed(); // Re-check after subscribing
      setIsSubscribed(confirmed);
    } catch (err) {
      console.error('Subscription failed:', err);
      alert('Failed to subscribe.');
    } finally {
      setIsProcessing(false);
    }
  };

  // -------------------- Unsubscribe from Notifications --------------------

  const handleUnsubscribe = async () => {
    try {
      setIsProcessing(true);
      await unsubscribeFromNotifications();
      const confirmed = await isUserSubscribed(); // Re-check after unsubscribing
      setIsSubscribed(confirmed);
    } catch (err) {
      console.error('Unsubscription failed:', err);
      alert('Failed to unsubscribe.');
    } finally {
      setIsProcessing(false);
    }
  };

  // -------------------- Render UI --------------------

  return (
    <div className="accessibility-bar">
      {/* 🔔 Notification toggle (only if supported) */}
      {isMessagingSupported && (
        isSubscribed === null ? (
          <span>🔄 Checking...</span>
        ) : isSubscribed ? (
          <button
            onClick={handleUnsubscribe}
            disabled={isProcessing}
            data-tooltip="Unsubscribe From Job Alert"
          >
            {isProcessing ? '⏳' : '🔕'}
          </button>
        ) : (
          <button
            onClick={handleSubscribe}
            disabled={isProcessing}
            data-tooltip="Get Job Alert"
          >
            {isProcessing ? '⏳' : '🔔'}
          </button>
        )
      )}

      {/* 🔤 Font size controls */}
      <button
        onClick={() => setFontSize(prev => Math.max(prev - 1, 8))}
        data-tooltip="Decrease Font"
      >
        A-
      </button>

      <button
        onClick={() => setFontSize(DEFAULT_FONT_SIZE)}
        disabled={fontSize === DEFAULT_FONT_SIZE}
        data-tooltip="Reset Font Size"
      >
        A
      </button>

      <button
        onClick={() => setFontSize(prev => Math.min(prev + 1, 24))}
        data-tooltip="Increase Font"
      >
        A+
      </button>

      {/* 🌗 Theme toggle */}
      <button
        onClick={() => setDarkMode(prev => !prev)}
        data-tooltip="Toggle Theme"
      >
        {darkMode ? '☀️' : '🌙'}
      </button>
    </div>
  );
};

export default AccessibilityBar;
